import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { HandHelping, MapPin, Flag, ArrowLeft } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAuthGate } from "@/components/AuthGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RequestCard } from "@/components/cards";
import { EmptyState, PageLoading } from "@/components/common";
import { ReportDialog } from "@/components/dialogs";

export function Requests() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/requests", { params: { limit: 40 } }).then(({ data }) => setItems(data.items)); }, []);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold tracking-tight">Community requests</h1>
        <Link to="/post/request"><Button size="sm" data-testid="requests-post-btn"><HandHelping className="h-4 w-4" />I need someone</Button></Link>
      </div>
      {items === null ? <PageLoading /> : items.length === 0 ? (
        <EmptyState icon={HandHelping} title="No requests yet" subtitle="Be the first to ask the community for help."
          action={<Link to="/post/request"><Button>Post a request</Button></Link>} />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">{items.map((r) => <RequestCard key={r.id} r={r} />)}</div>
      )}
    </div>
  );
}

export function RequestDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const gate = useAuthGate();
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [quote, setQuote] = useState("");
  const [report, setReport] = useState(false);
  const load = () => api.get(`/requests/${id}`).then(({ data }) => setData(data));
  useEffect(() => { load(); }, [id]);
  if (!data) return <PageLoading />;
  const r = data.request;
  const respond = async () => {
    if (!gate.require("Create a free account to respond to community requests.")) return;
    if (!msg.trim()) return;
    try { await api.post(`/requests/${id}/respond`, { message: msg, quote: quote ? parseFloat(quote) : null }); toast.success("Response sent"); setMsg(""); setQuote(""); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <button onClick={() => nav("/requests")} className="flex items-center gap-1 text-sm text-muted-foreground"><ArrowLeft className="h-4 w-4" />Back</button>
      <div className="rounded-2xl border border-border bg-card p-5">
        <span className="rounded-full bg-accent/10 px-2 py-0.5 text-xs font-semibold text-accent">{r.category}</span>
        <h1 className="mt-2 font-display text-xl font-bold">{r.title}</h1>
        <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{r.suburb ? `${r.suburb}, ` : ""}{r.state}{r.date_needed ? ` · ${r.date_needed}` : ""}{r.budget ? ` · Budget $${r.budget}` : ""}</p>
        {r.description && <p className="mt-3 text-sm text-foreground/80">{r.description}</p>}
        <Button variant="ghost" size="sm" className="mt-2" onClick={() => user ? setReport(true) : gate.require("Create a free account to report requests.")} data-testid="req-report"><Flag className="h-4 w-4" />Report</Button>
      </div>
      <div className="rounded-2xl border border-border bg-card p-5">
        <h2 className="font-display text-lg font-semibold">Respond to this request</h2>
        <div className="mt-3 space-y-2">
          <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="How can you help?" data-testid="req-response-msg" />
          <div className="flex gap-2">
            <Input type="number" value={quote} onChange={(e) => setQuote(e.target.value)} placeholder="Quote (optional)" data-testid="req-response-quote" />
            <Button onClick={respond} data-testid="req-respond-btn">Send response</Button>
          </div>
        </div>
        <div className="mt-4 space-y-3">
          {data.responses.map((resp) => (
            <div key={resp.id} className="border-t border-border pt-3">
              <div className="flex items-center justify-between"><span className="font-medium">{resp.responder?.name}</span>{resp.quote && <span className="font-semibold text-primary">${resp.quote}</span>}</div>
              <p className="mt-1 text-sm text-foreground/80">{resp.message}</p>
            </div>
          ))}
          {data.responses.length === 0 && <p className="text-sm text-muted-foreground">No responses yet.</p>}
        </div>
      </div>
      <ReportDialog open={report} onOpenChange={setReport} targetType="community_request" targetId={id} reportedUserId={r.owner_id} reportedUserName={r.requester?.name} onBlocked={() => nav("/requests")} />
    </div>
  );
}
